import { FormProvider } from 'react-hook-form';

import { ButtonType, ButtonVariant } from '@xemplo/button';
import { CheckboxRhf, CheckboxSize } from '@xemplo/checkbox';
import { DateTimeHelper } from '@xemplo/date-time';
import { HeaderIconMode } from '@xemplo/header-icon';
import { DawnCircleInformation } from '@xemplo/icons';
import { Modal, ModalDialogSlot } from '@xemplo/modal-dialog';

import {
  FINALISATION_DATE_FORMAT,
  FINALISE_PAYRUN_COPY,
  PAY_DATE_FORMAT,
} from '../finalise-payrun.const';
import * as S from '../finalise-payrun.styles';
import { FinalisePayrunContentProps } from '../finalise-payrun.types';

import { useFinalisePayrunForm } from './use-finalise-payrun-form';

export function FinalisePayrunContent({
  modalDialog,
  payrunId,
  payrun,
  settings,
  onSuccess,
}: Readonly<FinalisePayrunContentProps>) {
  const { methods, handleCancel, handleSubmit } = useFinalisePayrunForm({
    payrunId,
    settings,
    modalDialog,
    onSuccess,
  });

  const finalisationDate = DateTimeHelper.formatString(payrun.finaliseDueDateTime, {
    format: FINALISATION_DATE_FORMAT,
    fallback: '—',
    isUtc: false,
  });
  const payDate = DateTimeHelper.formatString(payrun.payDate, {
    format: PAY_DATE_FORMAT,
    fallback: '—',
    isUtc: false,
  });

  return (
    <Modal>
      <Modal.Slot
        name={ModalDialogSlot.CloseButton}
        render={
          <Modal.CloseButton
            onClick={handleCancel}
            id="finalise-payrun-close-button"
            ariaLabel="Close finalise pay run modal"
          />
        }
      />
      <Modal.Slot
        name={ModalDialogSlot.Header}
        render={
          <Modal.Header
            title={FINALISE_PAYRUN_COPY.title}
            headerIcon={{
              icon: <DawnCircleInformation />,
              mode: HeaderIconMode.Object,
            }}
          />
        }
      />
      <Modal.Slot
        name={ModalDialogSlot.Body}
        render={
          <Modal.Body>
            <FormProvider {...methods}>
              <S.Body>
                <S.Description>
                  <S.DescriptionHeading>
                    {FINALISE_PAYRUN_COPY.descriptionHeading}
                  </S.DescriptionHeading>
                  <S.DescriptionBody>
                    {FINALISE_PAYRUN_COPY.descriptionBody}
                  </S.DescriptionBody>
                </S.Description>
                <S.Card>
                  <S.Row>
                    <S.RowLabel>{FINALISE_PAYRUN_COPY.finalisationRowLabel}</S.RowLabel>
                    <S.RowValue>{finalisationDate}</S.RowValue>
                  </S.Row>
                  <S.Row>
                    <S.RowLabel>{FINALISE_PAYRUN_COPY.payDateRowLabel}</S.RowLabel>
                    <S.RowValue>{payDate}</S.RowValue>
                  </S.Row>
                </S.Card>
                <S.Checkboxes>
                  <S.CheckboxRow>
                    <S.CheckboxTick>
                      <CheckboxRhf
                        name="finaliseExternalPayrun"
                        id="finalise-external-payrun"
                        ariaLabel={FINALISE_PAYRUN_COPY.finaliseExternalLabel}
                        size={CheckboxSize.Small}
                      />
                    </S.CheckboxTick>
                    <S.CheckboxLabel htmlFor="finalise-external-payrun">
                      <S.CheckboxTitle>
                        {FINALISE_PAYRUN_COPY.finaliseExternalLabel}
                      </S.CheckboxTitle>
                      <S.CheckboxHint>
                        {FINALISE_PAYRUN_COPY.finaliseExternalHint}
                      </S.CheckboxHint>
                    </S.CheckboxLabel>
                  </S.CheckboxRow>
                  <S.CheckboxRow>
                    <S.CheckboxTick>
                      <CheckboxRhf
                        name="sendPayslips"
                        id="send-payslips"
                        ariaLabel={FINALISE_PAYRUN_COPY.sendPayslipsLabel}
                        size={CheckboxSize.Small}
                      />
                    </S.CheckboxTick>
                    <S.CheckboxLabel htmlFor="send-payslips">
                      <S.CheckboxTitle>
                        {FINALISE_PAYRUN_COPY.sendPayslipsLabel}
                      </S.CheckboxTitle>
                      <S.CheckboxHint>
                        {FINALISE_PAYRUN_COPY.sendPayslipsHint}
                      </S.CheckboxHint>
                    </S.CheckboxLabel>
                  </S.CheckboxRow>
                </S.Checkboxes>
              </S.Body>
            </FormProvider>
          </Modal.Body>
        }
      />
      <Modal.Slot
        name={ModalDialogSlot.Footer}
        render={
          <Modal.Footer
            btnPrimary={{
              onClickAsync: handleSubmit,
              label: FINALISE_PAYRUN_COPY.submit,
              ariaLabel: FINALISE_PAYRUN_COPY.submit,
              type: ButtonType.Primary,
            }}
            btnTertiary={{
              onClick: handleCancel,
              label: FINALISE_PAYRUN_COPY.cancel,
              ariaLabel: FINALISE_PAYRUN_COPY.cancel,
              type: ButtonType.Tertiary,
              variant: ButtonVariant.Dark,
            }}
          />
        }
      />
    </Modal>
  );
}
